// frontend/src/components/common/ReloadOnNewVersion.jsx
//
// Headless. Moves a long-open document onto the build that is deployed now.
//
// A loaded page keeps running the code it started with: a merchant who never
// reloads keeps an old layout, and old code calling an API that has since
// changed. This compares the build compiled into the page (__BUILD_ID__) with
// the one the static host serves (/version.json, both written by
// vite.config.js) and reloads only when the merchant is not looking at the
// page or is already leaving it — the tab is hidden, or they open another
// page — and only when the reload loses nothing: no in-app tab with unsaved
// edits (tabs/useDirtyTabs) and no open popup, since not every popup reports
// its typing there. Open tabs survive the reload (tabs/storage.js).
//
// The check runs on mount, every 10 minutes while the tab is visible, when it
// becomes visible again (the query client's focus refetch), and when it is
// hidden. A failed check is logged and leaves the page as it is until the
// next one.

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { appVersion } from '../../queries/keys';
import { useChangeEffect } from '../../hooks/useChangeEffect';
import { anyTabDirty } from '../../tabs/useDirtyTabs';

const TEN_MIN = 10 * 60 * 1000;

async function fetchDeployedBuild() {
  const res = await fetch('/version.json', { cache: 'no-store' });
  if (!res.ok) throw new Error(`/version.json answered ${res.status}`);
  return (await res.json()).build;
}

function reloadIfNewAndSafe(deployed) {
  const isNew = deployed !== undefined && deployed !== __BUILD_ID__;
  const popupOpen = document.querySelector('[role="dialog"]') !== null;
  if (isNew && !anyTabDirty() && !popupOpen) window.location.reload();
}

export default function ReloadOnNewVersion() {
  const { data: deployed, error, refetch } = useQuery({
    queryKey: appVersion.deployed(),
    queryFn: fetchDeployedBuild,
    refetchInterval: TEN_MIN,
  });

  useEffect(() => {
    if (error) console.error('ReloadOnNewVersion: version check failed', error);
  }, [error]);

  useEffect(() => {
    const onVisibilityChange = async () => {
      if (document.visibilityState !== 'hidden') return;
      const { data } = await refetch();
      // The merchant may have come back while the check was in flight.
      if (document.visibilityState === 'hidden') reloadIfNewAndSafe(data);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [refetch]);

  // The router has already moved to the new page, so the reload opens it.
  const { pathname } = useLocation();
  useChangeEffect(pathname, () => reloadIfNewAndSafe(deployed));

  return null;
}
